import { useContext, useMemo, useState } from 'react';
import { Box, Button, Card, Divider, Icon, Paragraph, Tag, Toast, useToast } from '@zillow/constellation-10';
import { IconCheckmarkCircleFilled, IconCheckmarkFilled, IconHideFilled } from '@zillow/constellation-icons';
import { TourActionButton, TourActionType, TourStatusForZcheduler } from '@zg-rentals/ts-z-calendar-client';
import getTourDateTime from '../../utils/getTourDateTime';
import getTourCalendarUrl from '../../utils/getTourCalendarUrl';
import StringConstants from '../../constants/StringConstants';
import useUpdateStatusLabelCache from '../../hooks/useUpdateStatusLabelCache';
import usePageIdentifier from '../../hooks/usePageIdentifier';
import useConsistentCancellationsUiEnabled from '../../hooks/useConsistentCancellationsUiEnabled';
import useManualTourSchedulingEnabled from '../../hooks/useManualTourSchedulingEnabled';
import { AppContext } from '../../contexts/AppContextProvider';
import type { TourActionTriggerSource } from '../../constants/clickstreamEvents/tourScheduling';
import { PAGE_NAME } from '../../constants/pageName';
import useTourActionClickstream from '../../hooks/useTourActionClickstream';
import AddTourButton from '../AddTourButton';
import TourCancellationModal from '../Modals/TourCancellationModal';
import TourStatusCardSkeleton from './Skeleton';
import { useAuth } from '../../AuthProvider';
import { FULLSTORY_EXCLUDE_CLASS_NAME } from '@zg-rentals/satellite-components';
import {
  getTourStatusTagLabel,
  getTourStatusTone,
  RescheduleTourModal,
  RescheduleTourModalTriggers,
  usePerformTourAction,
  useTourForSatelliteConversationId,
} from '@zg-rentals/touring';
import {
  cardStyles,
  defaultViewActionStyles,
  drawerFooterStyles,
  drawerSummaryStyles,
  redesignedCardStyles,
  stackedFooterStyles,
  stackedSummaryStyles,
  stackedViewActionStyles,
  strikethroughTextStyles,
} from './styles';

export type TourActionUI = {
  id: TourActionButton;
  text: string;
  icon: JSX.Element;
  onClick: () => void;
  action: TourActionTriggerSource;
};

type TourActionTone = 'brand' | 'critical' | 'neutral';

const getTourActionTone = (action: TourActionButton): TourActionTone => {
  if (action === TourActionButton.CONFIRM) return 'brand';
  if (action === TourActionButton.CANCEL) return 'critical';
  return 'neutral';
};

type TourStatusCardProps = {
  /** Offers the add-tour button when there's no tour; callers pass the lead's scheduling restriction. */
  canAddTour?: boolean;
  conversationId: string;
  leadId?: string | null;
  listingAlias: string;
  /** The landlord's notepad tour time, used to prefill the add-tour form. */
  notedTourStartMs?: number;
  // Drilled to TourCancellationModal (inbox: getMessages cache; lead management: lead row).
  renterName: string;
  showStackedView?: boolean;
  showViewInCalendarLink?: boolean;
};

export default function TourStatusCard({
  canAddTour = false,
  conversationId,
  leadId,
  listingAlias,
  notedTourStartMs,
  renterName,
  showStackedView,
  showViewInCalendarLink,
}: TourStatusCardProps) {
  const { isImpersonator } = useContext(AppContext);
  const isManualTourSchedulingEnabled = useManualTourSchedulingEnabled();
  const { enqueueToast } = useToast();
  const { user } = useAuth();
  const currentPage = usePageIdentifier();
  const isConsistentCancellationsUiEnabled = useConsistentCancellationsUiEnabled();
  const updateStatusLabelCache = useUpdateStatusLabelCache({
    conversationId,
    listingAlias,
  });
  const [showTourCancellationModal, setShowTourCancellationModal] = useState(false);
  const [showTourRescheduleModal, setShowTourRescheduleModal] = useState(false);
  const [tourActionForModal, setTourActionForModal] = useState<
    TourActionType.IRENE_CANCEL | TourActionType.IRENE_DECLINE
  >(TourActionType.IRENE_CANCEL);

  const { mutate: performTourAction } = usePerformTourAction({
    errorDetails: 'TourStatusCard - Error fetching perform tour action',
  });

  const {
    data: tourData,
    isLoading,
    refetch,
  } = useTourForSatelliteConversationId(
    {
      conversationId,
      listingAlias,
    },
    {
      errorDetails: 'TourStatusCard - Error fetching tour for conversationId',
    },
  );

  const { allowedLandlordActions, id, startMs, zoneId } = tourData?.tour || {};
  const { tourDateTimeCapitalized, tourDateTimeLowerCase } = useMemo(() => {
    if (!startMs || !zoneId) {
      return { tourDateTimeCapitalized: '', tourDateTimeLowerCase: '' };
    }

    const tourStartMs = startMs.toString();

    return {
      tourDateTimeCapitalized: getTourDateTime({ startMs: tourStartMs, timeZone: zoneId, capitalizeText: true }),
      tourDateTimeLowerCase: getTourDateTime({ startMs: tourStartMs, timeZone: zoneId, capitalizeText: false }),
    };
  }, [startMs, zoneId]);

  const { eventTypeId, fireTourActionEvent: fireClickstreamEvent } = useTourActionClickstream({ leadId, listingAlias });

  const handleConfirmTour = () => {
    fireClickstreamEvent('confirm_tour');

    performTourAction(
      {
        postData: {
          actionType: TourActionType.IRENE_CONFIRM,
          listingAlias,
          tourId: id!,
        },
      },
      {
        onSuccess: (response) => {
          const message = `Tour for ${tourDateTimeLowerCase || 'the scheduled time'} confirmed`;
          enqueueToast(<Toast tone="success">{message}</Toast>);
          const startMs = response?.tour?.startMs;
          if (startMs) {
            updateStatusLabelCache(TourActionType.IRENE_CONFIRM, startMs);
          }
        },
        onError: () => {
          enqueueToast(<Toast tone="critical">Tour couldn’t be confirmed. Wait and try again.</Toast>);
        },
      },
    );
  };

  const handleRescheduleSuccess = (startMs?: number) => {
    if (startMs) {
      updateStatusLabelCache(TourActionType.IRENE_CONFIRM, startMs);
      refetch();
    }
  };

  const handleRescheduleClick = () => {
    fireClickstreamEvent('reschedule_tour');
    setShowTourRescheduleModal(true);
  };

  const handleCancelClick = () => {
    fireClickstreamEvent('cancel_tour');
    setShowTourCancellationModal(true);
    setTourActionForModal(TourActionType.IRENE_CANCEL);
  };

  const handleDeclineClick = () => {
    fireClickstreamEvent('decline_tour');
    setShowTourCancellationModal(true);
    setTourActionForModal(TourActionType.IRENE_DECLINE);
  };

  const tourActionsUI: { [key in TourActionButton]: TourActionUI } = {
    [TourActionButton.CONFIRM]: {
      id: TourActionButton.CONFIRM,
      text: 'Confirm',
      icon: <IconCheckmarkFilled />,
      onClick: handleConfirmTour,
      action: 'confirm_tour',
    },
    [TourActionButton.IRENE_RESCHEDULE]: {
      id: TourActionButton.IRENE_RESCHEDULE,
      text: 'Reschedule',
      icon: <IconHideFilled />,
      onClick: handleRescheduleClick,
      action: 'reschedule_tour',
    },
    [TourActionButton.DECLINE]: {
      id: TourActionButton.DECLINE,
      text: 'Decline',
      icon: <IconHideFilled />,
      onClick: handleDeclineClick,
      action: 'decline_tour',
    },
    [TourActionButton.CANCEL]: {
      id: TourActionButton.CANCEL,
      // The stacked Messages sidebar lays actions out two-up, so it keeps the short label.
      text: showStackedView ? 'Cancel' : 'Cancel tour',
      icon: <IconHideFilled />,
      onClick: handleCancelClick,
      action: 'cancel_tour',
    },
  };

  const orderedActions = useMemo(() => {
    // Get the keys of tourActionsUI in the order they were defined
    return Object.keys(tourActionsUI) as Array<TourActionButton>;
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- preserving existing behavior; not re-verified
  }, [tourActionsUI]);

  const actions = useMemo(() => {
    if (!allowedLandlordActions || allowedLandlordActions.length === 0) {
      return [];
    }

    return orderedActions
      .filter((action) => allowedLandlordActions.includes(action))
      .map((action) => tourActionsUI[action]);
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- preserving existing behavior; not re-verified
  }, [allowedLandlordActions, orderedActions]);

  if (isLoading) return <TourStatusCardSkeleton showStackedView={showStackedView} />;
  if (!tourData?.tour) {
    // An undefined response means the lookup failed or never ran, so whether a tour exists is unknown.
    if (!tourData || !isManualTourSchedulingEnabled || !canAddTour) return null;

    return (
      <>
        <Paragraph textStyle="body-sm-bold" css={{ color: 'text.subtle', marginBottom: 'tighter' }}>
          Tour
        </Paragraph>
        <AddTourButton
          conversationId={conversationId}
          leadId={leadId}
          listingAlias={listingAlias}
          notedTourStartMs={notedTourStartMs}
        />
      </>
    );
  }
  const { id: tourId, tourStatus, title } = tourData.tour;
  const isTourConfirmed = tourStatus === TourStatusForZcheduler.CONFIRMED;
  const borderColor = isTourConfirmed ? 'blue.400' : 'gray.300';
  // The calendar never loads declined tours and hides cancelled ones by default, so the
  // deep link would dead-end in a "tour not found" toast for those statuses.
  const shouldShowViewInCalendarLink =
    !!showViewInCalendarLink &&
    tourStatus !== TourStatusForZcheduler.DECLINED &&
    tourStatus !== TourStatusForZcheduler.CANCELLED;
  const tourDateTimeText = tourDateTimeCapitalized || 'Tour time unavailable';

  const renderViewInCalendarLink = ({ spansRow, fillsRow }: { spansRow: boolean; fillsRow: boolean }) => (
    <Button
      asChild
      data-eventid={eventTypeId}
      size="sm"
      tone="neutral"
      emphasis="outlined"
      css={{
        gridColumn: spansRow ? '1 / -1' : 'auto',
        ...(fillsRow && { flexGrow: 1 }),
      }}
    >
      <a href={getTourCalendarUrl({ listingAlias, tourId })} onClick={() => fireClickstreamEvent('view_in_calendar')}>
        {StringConstants.VIEW_IN_CALENDAR}
      </a>
    </Button>
  );

  const renderRedesignedCard = () => {
    const isTourCancelled = tourStatus === TourStatusForZcheduler.CANCELLED;
    const statusTagLabel = getTourStatusTagLabel(tourStatus);
    const footerItemCount = actions.length + (shouldShowViewInCalendarLink ? 1 : 0);
    // A lone footer action fills the footer: the stacked sidebar is a grid at md and up, while the
    // drawer and the narrow sidebar are flex rows, so both properties are needed.
    const isSoleFooterItem = footerItemCount === 1;

    return (
      <Card elevated={false} css={redesignedCardStyles}>
        <Box css={showStackedView ? stackedSummaryStyles : drawerSummaryStyles}>
          <Box css={{ display: 'flex', flexDirection: 'column' }}>
            <Paragraph
              css={isTourCancelled ? strikethroughTextStyles : undefined}
              data-strikethrough={isTourCancelled || undefined}
              textStyle="body-lg-bold"
            >
              {StringConstants.IN_PERSON_TOUR}
            </Paragraph>
            <Paragraph
              css={{ color: 'text.subtle', ...(isTourCancelled && strikethroughTextStyles) }}
              data-strikethrough={isTourCancelled || undefined}
              textStyle="body"
            >
              {tourDateTimeText}
            </Paragraph>
          </Box>
          {statusTagLabel && (
            <Tag css={{ flexShrink: 0 }} size="sm" tone={getTourStatusTone(tourStatus)}>
              {statusTagLabel}
            </Tag>
          )}
        </Box>

        {footerItemCount > 0 && (
          <>
            <Divider />
            <Box css={showStackedView ? stackedFooterStyles : drawerFooterStyles}>
              {shouldShowViewInCalendarLink &&
                renderViewInCalendarLink({
                  spansRow: !!showStackedView || isSoleFooterItem,
                  fillsRow: isSoleFooterItem,
                })}
              {actions.map(({ id, text, onClick }) => {
                const isConfirmAction = id === TourActionButton.CONFIRM;
                // One flag drives the label and the mask together, so a nameless lead can never render a
                // dangling "Reschedule with " nor mask a button that has no name to hide.
                const showsRenterName =
                  isTourCancelled && id === TourActionButton.IRENE_RESCHEDULE && !!renterName.trim();
                const spansFooter = isSoleFooterItem || (!!showStackedView && isConfirmAction);

                return (
                  <Button
                    className={showsRenterName ? FULLSTORY_EXCLUDE_CLASS_NAME : undefined}
                    data-eventid={eventTypeId}
                    key={text}
                    size="sm"
                    onClick={onClick}
                    disabled={isImpersonator}
                    tone={getTourActionTone(id)}
                    emphasis={isConfirmAction ? 'filled' : 'outlined'}
                    css={{
                      gridColumn: spansFooter ? '1 / -1' : 'auto',
                      flexGrow: isSoleFooterItem ? 1 : 0,
                    }}
                  >
                    {showsRenterName ? `Reschedule with ${renterName}` : text}
                  </Button>
                );
              })}
            </Box>
          </>
        )}
      </Card>
    );
  };

  const renderTourSummary = () => {
    if (isTourConfirmed) {
      return (
        <Box
          css={{
            display: 'grid',
            gridTemplateColumns: { base: '5% auto', md: '8% auto' },
            alignItems: 'center',
            gap: 'tighter',
          }}
        >
          {/* oxlint-disable-next-line constellation/no-raw-color-values */}
          <Icon size="sm" css={{ color: 'green' }}>
            <IconCheckmarkCircleFilled />
          </Icon>
          <Paragraph textStyle="body-sm-bold">{title}</Paragraph>
          <Paragraph css={{ gridColumn: '2' }} textStyle="body-sm">
            {tourDateTimeText}
          </Paragraph>
        </Box>
      );
    }

    return (
      <Box css={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <Paragraph textStyle="body-sm-bold">{title}</Paragraph>
        <Paragraph textStyle="body-sm">{tourDateTimeText}</Paragraph>
      </Box>
    );
  };

  const renderActions = () => (
    <Box css={showStackedView ? stackedViewActionStyles : defaultViewActionStyles}>
      {shouldShowViewInCalendarLink && renderViewInCalendarLink({ spansRow: !!showStackedView, fillsRow: false })}
      {actions.map(({ id, text, onClick }) => {
        const isConfirmAction = id === TourActionButton.CONFIRM;

        return (
          <Button
            data-eventid={eventTypeId}
            key={text}
            size="sm"
            onClick={onClick}
            disabled={isImpersonator}
            tone={getTourActionTone(id)}
            emphasis={isConfirmAction ? 'filled' : 'outlined'}
            css={{ gridColumn: showStackedView && isConfirmAction ? '1 / -1' : 'auto' }}
          >
            {text}
          </Button>
        );
      })}
    </Box>
  );

  const renderLegacyCard = () => (
    <Card
      elevated={false}
      css={{
        borderLeftColor: borderColor,
        ...cardStyles,
        ...(!showStackedView && {
          md: {
            flexDirection: 'row',
          },
        }),
      }}
    >
      {renderTourSummary()}

      {(!!actions.length || shouldShowViewInCalendarLink) && renderActions()}
    </Card>
  );

  return (
    <>
      {showTourCancellationModal && (
        <TourCancellationModal
          action={tourActionForModal}
          conversationId={conversationId}
          leadId={leadId}
          listingAlias={listingAlias}
          onClose={() => setShowTourCancellationModal(false)}
          renterName={renterName}
          shouldShowCancellationReasons={false}
          tourDateTime={tourDateTimeLowerCase}
          tourId={tourId}
        />
      )}

      {showTourRescheduleModal && (
        <RescheduleTourModal
          clickStreamUser={user}
          listingAlias={listingAlias}
          tourId={tourData?.tour?.id}
          tourTitle={tourData?.tour?.title}
          triggerSource={
            currentPage === PAGE_NAME.leadManagement
              ? RescheduleTourModalTriggers.LEAD_MANAGEMENT
              : RescheduleTourModalTriggers.SATELLITE
          }
          onClose={() => setShowTourRescheduleModal(false)}
          onRescheduleSuccess={handleRescheduleSuccess}
        />
      )}

      {isConsistentCancellationsUiEnabled ? renderRedesignedCard() : renderLegacyCard()}
    </>
  );
}
